import { useMutation } from '@apollo/client/react';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
} from 'antd';
import { filter, forEach, keys, map, toLower } from 'lodash';
import React, { useContext, useState } from 'react';
import { useMedia } from 'react-use';
import { toast } from '../apollo';
import { AppContext } from '../AppContext';
import { DeleteButton } from '../assets/svg';
import { BREAKPOINTS, TYPES, TYPES_LABELS } from '../common/constants';
import { Event } from '../common/trackEvents';
import { formValidatorRules } from '../common/utils';
import CommonSelect from './CommonSelect';
import LineNumberedTextArea from './LineNumberedTextArea';

const { required, characterWithoutWhiteSpace } = formValidatorRules;

const OptionUi = (isDesktopViewport) => {
  return (
    <>
      <Form.List name="options">
        {(fields, { add, remove }) => {
          return (
            <Row gutter={[15, 15]} className="width-percent-100">
              {fields.map((field) => (
                <Col span={24} key={field.key}>
                  <Row gutter={[30]}>
                    <Col flex="auto">
                      <Form.Item
                        {...field}
                        label="Value"
                        name={[field.name, 'option']}
                        fieldKey={[field.fieldKey, 'option']}
                        validateTrigger={['onChange', 'onClick']}
                        rules={[
                          required,
                          characterWithoutWhiteSpace,
                          {
                            max: 30,
                            message: 'Option cannot be more than 30 characters',
                          },
                          ({ getFieldValue }) => ({
                            validator(rule, value) {
                              const trimmedValue = value?.trim();
                              const items = map(
                                getFieldValue()?.options,
                                (item) => item?.option?.trim(),
                              );
                              if (
                                filter(
                                  items,
                                  (val) =>
                                    toLower(val) === toLower(trimmedValue),
                                )?.length > 1 &&
                                trimmedValue
                              ) {
                                // eslint-disable-next-line prefer-promise-reject-errors
                                return Promise.reject(
                                  'should be a unique option',
                                );
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                      >
                        <Input placeholder="Enter Value" />
                      </Form.Item>
                    </Col>
                    <Col className="d-flex align-center">
                      <Form.Item
                        {...field}
                        label={isDesktopViewport && ' '}
                        name={[field.name, 'qcFail']}
                        fieldKey={[field.fieldKey, 'qcFail']}
                        valuePropName="checked"
                        className="mr-10 mb-0"
                      >
                        <Checkbox>Mark for QC fail</Checkbox>
                      </Form.Item>
                      {fields?.length > 2 && (
                        <Form.Item
                          label={isDesktopViewport && ' '}
                          className="mb-0"
                        >
                          <DeleteButton
                            className="pointer"
                            onClick={() => {
                              if (fields?.length > 2) remove(field?.name);
                            }}
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                </Col>
              ))}
              <Row>
                <Col className="mb-15">
                  <Button
                    className="grey-button"
                    shape="round"
                    onClick={(e) => {
                      if (fields?.length >= 20) {
                        e?.preventDefault();
                        message.destroy();
                        message.error('You can only add up to 20 options.');
                        return;
                      }
                      add();
                    }}
                    block
                  >
                    Add Option
                  </Button>
                </Col>
              </Row>
            </Row>
          );
        }}
      </Form.List>
    </>
  );
};

const AddMultipleModal = ({
  showModal,
  setShowModal,
  stageId,
  stageIdFieldName,
  refetchStageData,
  mutation,
  mutationResponseField,
  analyticsEvent,
  analyticsLabel,
  analyticsContextId,
  analyticsContextIdField,
  modalTitle,
  onJobStarted,
}) => {
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [form] = Form.useForm();
  const [checkpoints, setCheckpoints] = useState('');
  const [parsedCheckpoints, setParsedCheckpoints] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [inputType, setInputType] = useState(TYPES['Y/N']);
  const [photoRequired, setPhotoRequired] = useState(false);
  const [remarkRequired, setRemarkRequired] = useState(false);
  const [popconfirmVisible, setPopconfirmVisible] = useState(false);

  const handleCancel = () => {
    setShowModal(false);
    form.resetFields();
    setCheckpoints('');
    setParsedCheckpoints([]);
    setValidationError('');
    setInputType(TYPES['Y/N']);
    setPhotoRequired(false);
    setRemarkRequired(false);
    setPopconfirmVisible(false);
  };

  const [bulkCreateStageItems] = useMutation(mutation, {
    onError() {},
    onCompleted(data) {
      const { status, jobId } = data?.[mutationResponseField] || {};

      if (status === 'SUCCESS') {
        const analyticsData = {
          label: analyticsLabel,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          stage_id: stageId,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        };
        analyticsData[analyticsContextIdField] = analyticsContextId;
        Event(analyticsEvent, analyticsData);

        if (jobId) {
          onJobStarted?.(jobId);
        } else {
          refetchStageData?.();
        }
        handleCancel();
      }
    },
  });

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];

  const handleInputTypeChange = (value) => {
    setInputType(value);

    if (value === TYPES.OPTION) {
      form.setFieldsValue({ options: [{}, { qcFail: true }] });
    } else {
      form.setFieldsValue({ options: [] });
    }
  };

  const handlePhotosRequiredChange = (value) => {
    setPhotoRequired(value);
  };

  const handleRemarksRequiredChange = (value) => {
    setRemarkRequired(value);
  };

  const parseCheckpoints = (text) => {
    if (!text?.trim()) return [];

    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return lines.map((line) => line.replace(/\t+/g, ' '));
  };

  const validateCheckpoints = (text) => {
    if (!text || typeof text !== 'string') {
      return 'Add at least one checkpoint.';
    }

    const parsed = parseCheckpoints(text);

    // Check if at least one non-empty line exists
    if (parsed?.length === 0) {
      return 'Add at least one checkpoint.';
    }

    // Check bulk limit of 50 checkpoints
    if (parsed?.length > 100) {
      return `Maximum 100 checkpoints allowed. You have ${parsed?.length} checkpoints.`;
    }

    return '';
  };

  const handleTextChange = (e) => {
    const text = e?.target?.value || '';
    setCheckpoints(text);

    const parsed = parseCheckpoints(text);
    setParsedCheckpoints(parsed);

    // Validate and set error message
    const error = validateCheckpoints(text);
    setValidationError(error);

    // Update form field error status
    if (error) {
      form.setFields([
        {
          name: 'checkpoints',
          errors: [error],
        },
      ]);
    } else {
      form.setFields([
        {
          name: 'checkpoints',
          errors: [],
        },
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = e.target;
      const cursorPosition = textarea.selectionStart;
      const textBeforeCursor = checkpoints.substring(0, cursorPosition);
      const currentLineStart = textBeforeCursor.lastIndexOf('\n') + 1;
      const currentLine = textBeforeCursor.substring(currentLineStart);

      // If current line is empty or only whitespace, prevent Enter
      if (currentLine.trim() === '') {
        e.preventDefault();
      }
    }
  };

  const handlePreview = () => {
    // Check for line errors directly from checkpoints
    const lines = parseCheckpoints(checkpoints);
    if (lines?.some((item) => item?.length > 250)) {
      toast({
        message:
          'Please fix the checkpoints exceeding the 250 character limit before adding.',
        type: 'error',
      });
      return;
    }

    form
      .validateFields(['checkpoints'])
      .then(() => {
        const parsed = parseCheckpoints(checkpoints);
        setParsedCheckpoints(parsed);
        setValidationError('');
        setPopconfirmVisible(true);
      })
      .catch(() => {
        // Form validation failed, errors will be shown automatically
        const error = validateCheckpoints(checkpoints);
        setValidationError(error);
      });
  };

  const onFormSubmit = async (formValues) => {
    const options = [];

    if (formValues?.type === TYPES.OPTION) {
      forEach(formValues?.options, (option) => {
        const typeOptionValue = {
          option: option?.option,
          qcFail: option?.qcFail || false,
        };
        options.push(typeOptionValue);
      });
      const checkCheckedValue = (value) => {
        return value.qcFail === false;
      };
      const isValid = options.some(checkCheckedValue);
      if (!isValid) {
        toast({
          message: 'At least one option should be marked as QcPass',
          type: 'error',
        });
        return;
      }
    }

    const newFormValues = {
      [stageIdFieldName]: Number(stageId),
      titles: parsedCheckpoints,
      type: formValues?.type,
      photo: formValues?.photo,
      remark: formValues?.remark,
      options,
    };

    try {
      await bulkCreateStageItems({
        variables: {
          data: { ...newFormValues },
        },
      });
    } catch (error) {
      return error;
    }
  };

  return (
    <Modal
      maskClosable={false}
      centered
      open={showModal}
      className="add-multiple-modal"
      footer={null}
      closable={false}
      // eslint-disable-next-line no-nested-ternary
      width={isDesktopViewport ? 700 : '90vw'}
    >
      <>
        <h2 className="mb-15">{modalTitle}</h2>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: TYPES['Y/N'],
            photo: false,
            remark: false,
            options: [{}, { qcFail: true }],
          }}
          onFinish={onFormSubmit}
        >
          <Form.Item
            label="Checklist Points"
            name="checkpoints"
            validateStatus={validationError ? 'error' : ''}
            help={validationError}
            rules={[
              {
                validator: (_, value) => {
                  const error = validateCheckpoints(value || '');
                  if (error) {
                    return Promise.reject(new Error(error));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <LineNumberedTextArea
              placeholder={`Important notes:\na. Enter each checkpoint on a new line.\nb. Duplicate checkpoints will be skipped.\nc. Maximum 100 checkpoints can be added at a time.\nd. Each checkpoint can have up to 250 characters.`}
              value={checkpoints}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              rows={8}
              showCount={false}
              maxLength={25100}
            />
          </Form.Item>

          <h4 className="mb-10">Defaults (apply to all)</h4>
          <Row gutter={16}>
            <Col span={isDesktopViewport ? 8 : 24}>
              <Form.Item name="type" label="Type">
                <CommonSelect
                  placeholder="Select"
                  options={[
                    ...keys(TYPES_LABELS).map((type) => {
                      return {
                        key: TYPES_LABELS[type],
                        value: type,
                        label: TYPES_LABELS[type],
                      };
                    }),
                  ]}
                  value={inputType}
                  onChange={handleInputTypeChange}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 8 : 24}>
              <Form.Item name="photo" label="EQC Photo Required">
                <CommonSelect
                  placeholder="Select"
                  options={yesNoOptions}
                  value={photoRequired}
                  onChange={handlePhotosRequiredChange}
                />
              </Form.Item>
            </Col>
            <Col span={isDesktopViewport ? 8 : 24}>
              <Form.Item name="remark" label="Remarks Required">
                <CommonSelect
                  placeholder="Select"
                  options={yesNoOptions}
                  value={remarkRequired}
                  onChange={handleRemarksRequiredChange}
                />
              </Form.Item>
            </Col>
          </Row>

          {inputType === TYPES.OPTION && (
            <>
              <Divider dashed />
              <h3>Options</h3>
              {OptionUi(isDesktopViewport)}
            </>
          )}

          {parsedCheckpoints?.length > 0 && (
            <div className="mb-15">
              <p className="text-secondary">
                {parsedCheckpoints?.length} checkpoint
                {parsedCheckpoints?.length !== 1 ? 's' : ''} will be created
              </p>
            </div>
          )}

          <div className="form-buttons d-flex justify-end">
            <Button
              shape="round"
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Popconfirm
              title={`Do you want to add ${parsedCheckpoints?.length} checkpoint${parsedCheckpoints?.length !== 1 ? 's' : ''}?`}
              description="Existing or duplicate checkpoints will be automatically skipped."
              okText="Yes"
              placement="topRight"
              open={popconfirmVisible}
              onCancel={() => setPopconfirmVisible(false)}
              onConfirm={() => {
                setPopconfirmVisible(false);
                form.submit();
              }}
              okButtonProps={{
                shape: 'round',
                htmlType: 'submit',
              }}
              cancelText="No"
              cancelButtonProps={{ shape: 'round' }}
            >
              <Button
                shape="round"
                type="primary"
                className="preview-button"
                onClick={handlePreview}
                disabled={parsedCheckpoints?.length === 0 || !!validationError}
              >
                Add
              </Button>
            </Popconfirm>
          </div>
        </Form>
      </>
    </Modal>
  );
};

export default AddMultipleModal;
